/*
 * Lunvelis Partners · capa funcional compartida.
 *   1. Avisos globales (éxito, error, advertencia, información, procesamiento) sin alertas nativas.
 *   2. Registro local de solicitudes enviadas y borradores.
 *   3. Panel de cuenta (conecta con las cuentas reales de Shopify; no simula autenticación).
 *   4. Enlaces externos: apertura segura y ayuda cuando un canal no carga.
 *   5. Introducción de entrada: una vez por sesión, omitible.
 * Sin dependencias. Todo es mejora progresiva: el sitio funciona igual sin este archivo.
 */
(() => {
  if (window.LunvelisUI) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- almacenamiento seguro ---------- */
  const store = {
    get(key, fallback = null) { try { const raw = localStorage.getItem(key); return raw === null ? fallback : JSON.parse(raw); } catch (error) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (error) { return false; } },
    remove(key) { try { localStorage.removeItem(key); } catch (error) { /* sin almacenamiento */ } }
  };

  /* ---------- sonido de confirmación ---------- */
  let audio;
  const soundOn = () => store.get('lp.sound', 'on') !== 'off';
  const setSound = (on) => { store.set('lp.sound', on ? 'on' : 'off'); document.dispatchEvent(new CustomEvent('lp:sound')); };
  const tone = (ctx, freq, start, length, gain = 0.11, type = 'sine') => {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    const t0 = ctx.currentTime + start;
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    amp.gain.setValueAtTime(0.0001, t0);
    amp.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
    amp.gain.exponentialRampToValueAtTime(0.0001, t0 + length);
    osc.connect(amp).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + length + 0.05);
  };
  const playSound = (kind = 'success') => {
    if (!soundOn()) return;
    try {
      audio = audio || new (window.AudioContext || window.webkitAudioContext)();
      if (audio.state === 'suspended') audio.resume();
      if (kind === 'success') [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => tone(audio, freq, i * 0.09, 0.42));
      else if (kind === 'error') { tone(audio, 196, 0, 0.2, 0.12, 'triangle'); tone(audio, 164.81, 0.22, 0.3, 0.12, 'triangle'); }
      else tone(audio, 880, 0, 0.24, 0.08);
      if (navigator.vibrate) navigator.vibrate(kind === 'success' ? [18, 40, 18] : 24);
    } catch (error) { /* sin audio disponible */ }
  };

  /* ---------- avisos ---------- */
  const ICON = {
    success: '<path d="M5 12.5l4.2 4.2L19 7"/>',
    error: '<path d="M6 6l12 12M18 6L6 18"/>',
    warning: '<path d="M12 6.5v7M12 17.5h.01"/>',
    info: '<path d="M12 10.5v6.5M12 7h.01"/>',
    progress: '<path d="M12 3a9 9 0 1 0 9 9" />'
  };
  let region;
  const ensureRegion = () => {
    region = region || document.getElementById('lp-toasts');
    if (!region) {
      region = document.createElement('div');
      region.id = 'lp-toasts';
      region.className = 'lp-toasts';
      region.setAttribute('role', 'region');
      region.setAttribute('aria-label', 'Notificaciones');
      document.body.append(region);
    }
    return region;
  };
  const toasts = new Map();
  const close = (node) => {
    if (!node || node.classList.contains('is-leaving')) return;
    node.classList.add('is-leaving');
    const done = () => { node.remove(); for (const [key, value] of toasts) if (value.node === node) toasts.delete(key); };
    if (reduced.matches) done(); else { node.addEventListener('animationend', done, { once: true }); setTimeout(done, 400); }
  };
  const toast = (options = {}) => {
    const { type = 'info', title = '', message = '', action = null, duration, id } = options;
    const host = ensureRegion();
    if (options.sound) playSound(options.sound);
    const key = id || `${type}:${title}:${message}`;
    if (toasts.has(key)) close(toasts.get(key).node);
    const node = document.createElement('div');
    node.className = `lp-toast lp-toast--${type}`;
    node.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
    const icon = document.createElement('span');
    icon.className = 'lp-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `<svg viewBox="0 0 24 24">${ICON[type] || ICON.info}</svg>`;
    const body = document.createElement('div');
    body.className = 'lp-toast__body';
    const strong = document.createElement('strong');
    strong.textContent = title;
    body.append(strong);
    if (message) { const p = document.createElement('p'); p.textContent = message; body.append(p); }
    if (action && action.label) {
      const row = document.createElement('div');
      row.className = 'lp-toast__actions';
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = action.label;
      button.addEventListener('click', () => { try { action.onClick && action.onClick(); } finally { close(node); } });
      row.append(button);
      body.append(row);
    }
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'lp-toast__close';
    closeButton.setAttribute('aria-label', 'Cerrar notificación');
    closeButton.textContent = '×';
    closeButton.addEventListener('click', () => close(node));
    node.append(icon, body, closeButton);
    host.append(node);
    const live = () => [...host.children].filter((child) => !child.classList.contains('is-leaving'));
    for (let guard = 0; live().length > 4 && guard < 20; guard += 1) close(live()[0]);
    const ms = duration ?? (type === 'progress' ? 0 : type === 'error' ? 10000 : action ? 12000 : 6500);
    let timer = ms ? setTimeout(() => close(node), ms) : null;
    node.addEventListener('mouseenter', () => clearTimeout(timer));
    node.addEventListener('mouseleave', () => { if (ms) timer = setTimeout(() => close(node), 2500); });
    const handle = {
      node,
      close: () => close(node),
      update: (next = {}) => {
        if (next.type) { node.className = `lp-toast lp-toast--${next.type}`; icon.innerHTML = `<svg viewBox="0 0 24 24">${ICON[next.type] || ICON.info}</svg>`; node.setAttribute('role', next.type === 'error' ? 'alert' : 'status'); }
        if (next.title !== undefined) strong.textContent = next.title;
        if (next.message !== undefined) { let p = body.querySelector('p'); if (!p) { p = document.createElement('p'); strong.after(p); } p.textContent = next.message; }
        clearTimeout(timer);
        const after = next.duration ?? (next.type === 'error' ? 10000 : 6500);
        if (next.type && next.type !== 'progress') timer = setTimeout(() => close(node), after);
      }
    };
    toasts.set(key, handle);
    return handle;
  };

  /* ---------- solicitudes locales ---------- */
  const REQUESTS_KEY = 'lp.requests.v1';
  const requests = {
    list(kind) { const now = Date.now(); return (store.get(REQUESTS_KEY, []) || []).filter((item) => now - item.ts < 90 * 864e5 && (!kind || item.kind === kind)); },
    add(entry) { const list = this.list(); list.unshift({ ts: Date.now(), ...entry }); store.set(REQUESTS_KEY, list.slice(0, 30)); document.dispatchEvent(new CustomEvent('lp:requests')); return list[0].ts; },
    update(ts, patch) { store.set(REQUESTS_KEY, this.list().map((item) => (item.ts === ts ? { ...item, ...patch } : item))); document.dispatchEvent(new CustomEvent('lp:requests')); },
    remove(ts) { store.set(REQUESTS_KEY, this.list().filter((item) => item.ts !== ts)); document.dispatchEvent(new CustomEvent('lp:requests')); },
    clear() { store.remove(REQUESTS_KEY); document.dispatchEvent(new CustomEvent('lp:requests')); }
  };
  const when = (ts) => new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ts));

  /* Archivo de calendario (.ics) de una solicitud de reunión: recordatorio del horario pedido, marcado como tentativo. */
  const pad = (n) => String(n).padStart(2, '0');
  const icsDate = (date) => `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}00Z`;
  const icsText = (text) => String(text).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (m) => '\\' + m);
  const downloadIcs = (event) => {
    const body = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Lunvelis Partners//ES', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT', `UID:${Date.parse(event.start)}-${Math.random().toString(36).slice(2)}@lunvelispartners.world`, `DTSTAMP:${icsDate(new Date())}`, `DTSTART:${icsDate(new Date(event.start))}`, `DTEND:${icsDate(new Date(event.end))}`, `SUMMARY:${icsText(event.summary)}`, `DESCRIPTION:${icsText(event.description)}`, 'STATUS:TENTATIVE', 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/calendar;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url; link.download = 'solicitud-reunion-lunvelis.ics';
    document.body.append(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const paintRequests = () => {
    for (const list of document.querySelectorAll('[data-lp-requests]')) {
      const kind = list.dataset.kind || '';
      const items = requests.list(kind);
      list.replaceChildren();
      if (!items.length) { const li = document.createElement('li'); const small = document.createElement('small'); small.textContent = kind === 'meeting' ? 'Aún no has solicitado reuniones desde este dispositivo. Cuando envíes una, aparecerá aquí.' : 'Aún no has enviado solicitudes desde este dispositivo.'; li.append(small); list.append(li); continue; }
      for (const item of items) {
        const li = document.createElement('li');
        const strong = document.createElement('strong'); strong.textContent = item.title;
        const small = document.createElement('small'); small.textContent = `${item.status || 'Enviada'} · ${when(item.ts)}`;
        li.append(strong);
        if (item.detail) { const detail = document.createElement('span'); detail.className = 'lp-requests__detail'; detail.textContent = item.detail; li.append(detail); }
        li.append(small);
        if (item.ics || kind === 'meeting') {
          const row = document.createElement('div'); row.className = 'lp-requests__actions';
          if (item.ics) { const add = document.createElement('button'); add.type = 'button'; add.textContent = 'Añadir al calendario'; add.dataset.lpIcs = String(item.ts); row.append(add); }
          const del = document.createElement('button'); del.type = 'button'; del.textContent = 'Quitar del registro'; del.dataset.lpReqRemove = String(item.ts); row.append(del);
          li.append(row);
        }
        list.append(li);
      }
    }
  };
  document.addEventListener('lp:requests', paintRequests);
  document.addEventListener('click', (event) => {
    const ics = event.target.closest && event.target.closest('[data-lp-ics]');
    if (ics) { const item = requests.list().find((entry) => String(entry.ts) === ics.dataset.lpIcs); if (item && item.ics) { downloadIcs(item.ics); toast({ type: 'success', title: 'Calendario descargado', message: 'Es un recordatorio tentativo: la reunión se confirma por correo.', duration: 4000 }); } return; }
    const del = event.target.closest && event.target.closest('[data-lp-req-remove]');
    if (del) { requests.remove(Number(del.dataset.lpReqRemove)); toast({ type: 'info', title: 'Quitado de tu registro', message: 'Solo se borra la copia de este dispositivo.', duration: 3000 }); return; }
    const mute = event.target.closest && event.target.closest('[data-lp-sound-toggle]');
    if (mute) { setSound(!soundOn()); if (soundOn()) playSound('info'); }
  });
  document.addEventListener('lp:sound', () => { document.querySelectorAll('[data-lp-sound-toggle]').forEach((button) => { button.textContent = soundOn() ? 'Sonido de confirmación: activado' : 'Sonido de confirmación: silenciado'; button.setAttribute('aria-pressed', String(soundOn())); }); });

  /* ---------- panel de cuenta ---------- */
  const setupAccount = () => {
    const dialog = document.getElementById('lp-account');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    let opener = null;
    const open = (from) => { opener = from || document.activeElement; paintRequests(); if (!dialog.open) dialog.showModal(); };
    const shut = () => { if (dialog.open) dialog.close(); };
    /* En window y en captura: corre antes que los manejadores de la plantilla base (que se registran en document). */
    window.addEventListener('click', (event) => {
      const path = event.composedPath ? event.composedPath() : [event.target];
      const trigger = path.find((node) => node instanceof Element && (node.matches('.account-button, [data-lp-account-open]') || (node.matches('a[href]') && /^\/account\/?$/.test(new URL(node.href, location.origin).pathname) && !node.closest('#lp-account'))));
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open(trigger);
    }, true);
    dialog.addEventListener('click', (event) => { if (event.target === dialog || event.target.closest('[data-lp-close]')) shut(); });
    dialog.addEventListener('close', () => { if (opener && opener.focus) opener.focus(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') shut(); });
    const clear = dialog.querySelector('[data-lp-requests-clear]');
    if (clear) clear.addEventListener('click', () => { requests.clear(); toast({ type: 'info', title: 'Historial local borrado', message: 'Solo se eliminó la lista de este dispositivo; las solicitudes ya enviadas siguen con el equipo.' }); });
    if (location.hash === '#cuenta') open();
  };

  /* ---------- enlaces externos ---------- */
  const setupLinks = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest('a[href]');
      if (!link) return;
      let url;
      try { url = new URL(link.href, location.href); } catch (error) { return; }
      if (url.origin === location.origin || !/^https?:$/.test(url.protocol)) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (!/(tiktok|instagram|wa\.me|whatsapp)/.test(url.hostname)) return;
      /* Los perfiles sociales se abren en una pestaña nueva sin referrer: algunas plataformas fallan al cargar con él. */
      link.setAttribute('rel', 'noopener noreferrer');
      link.setAttribute('target', '_blank');
      const label = /tiktok/.test(url.hostname) ? 'TikTok' : /instagram/.test(url.hostname) ? 'Instagram' : 'WhatsApp';
      if (label === 'WhatsApp') return;
      setTimeout(() => toast({
        type: 'info', id: `ext-${label}`, title: `Abriendo ${label}`,
        message: `Si ${label} no carga en tu navegador, copia el enlace y ábrelo desde la app oficial.`,
        action: { label: 'Copiar enlace', onClick: () => { (navigator.clipboard ? navigator.clipboard.writeText(url.href) : Promise.reject()).then(() => toast({ type: 'success', title: 'Enlace copiado', message: url.href }), () => toast({ type: 'warning', title: 'No se pudo copiar', message: url.href })); } }
      }), 300);
    }, true);
  };

  /* ---------- introducción ---------- */
  const setupIntro = () => {
    const intro = document.getElementById('lp-intro');
    if (!intro) return;
    const finish = () => {
      if (intro.dataset.done) return;
      intro.dataset.done = '1';
      document.documentElement.classList.remove('lp-intro-on');
      intro.classList.add('is-out');
      setTimeout(() => intro.remove(), reduced.matches ? 0 : 1100);
    };
    intro.querySelector('[data-lp-intro-skip]')?.addEventListener('click', finish);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') finish(); });
    setTimeout(finish, 2300);
  };

  /* ---------- contenido de la página: aviso de envío confirmado por Shopify ---------- */
  const notePosted = () => {
    if (!/[?&]contact_posted=true/.test(location.search)) return;
    if (store.get('lp.pending.v1')) return;
    const done = () => toast({ type: 'success', sound: 'success', title: 'Solicitud recibida', message: 'Shopify confirmó el envío. El equipo de Lunvelis la revisará y te responderá por el correo que indicaste.' });
    setTimeout(done, 600);
    const clean = location.pathname + location.search.replace(/([?&])contact_posted=true&?/, '$1').replace(/[?&]$/, '') + location.hash;
    try { history.replaceState(null, '', clean); } catch (error) { /* sin historial */ }
  };

  window.LunvelisUI = { toast, store, requests, when, sound: playSound, soundOn, setSound, downloadIcs };
  const boot = () => { document.dispatchEvent(new CustomEvent('lp:sound')); ensureRegion(); setupAccount(); setupLinks(); setupIntro(); paintRequests(); notePosted(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
